// pages/AnimePage.tsx
import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import api from '../api/axios';
import {useAuth} from '../auth/AuthContext';
import '../styles/AnimePage.css';
import {Heart, Star, Eye} from 'lucide-react'

interface Anime {
    id: number;
    title: string;
    imageUrl: string;
    episodes: number;
    rating: number;
    genres: string[];
    studios: string[];
    description: string;
    releaseDate: string;
    animeStatus: string;
    season?: string;
    ageRating: string;
    sourceMaterial: string;
}

interface UserAnimeStatus {
    status: string;
    rating: number | null;
    episodesWatched: number;
    isFavorite: boolean;
}

const AnimePage: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const {isAuthenticated} = useAuth();
    const [anime, setAnime] = useState<Anime | null>(null);
    const [loading, setLoading] = useState(true);
    const [userStatus, setUserStatus] = useState<UserAnimeStatus | null>(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showEpisodesModal, setShowEpisodesModal] = useState(false);
    const [episodesInput, setEpisodesInput] = useState('');
    const statusOptions = ['WATCHING', 'COMPLETED', 'PLANNED', 'DROPPED', 'REWATCHING'];
    const [hoverRating, setHoverRating] = useState(0);
    const [isFavorite, setIsFavorite] = useState(false);
    const [hoverAgeRating, setHoverAgeRating] = useState(false);
    const [selectedEpisode, setSelectedEpisode] = useState<number>(0);
    const episodesListRef = useRef<HTMLDivElement>(null);

    // Функция для прокрутки к выбранному эпизоду
    const scrollToSelectedEpisode = useCallback(() => {
        if (episodesListRef.current && selectedEpisode) {
            const episodeElement = episodesListRef.current.querySelector(
                `.episode-item[data-episode="${selectedEpisode}"]`
            );

            if (episodeElement) {
                episodeElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });
            }
        }
    }, [selectedEpisode]);

    // Эффект для прокрутки при открытии модального окна
    useEffect(() => {
        if (showEpisodesModal && selectedEpisode) {
            // Даем время на рендер списка, затем прокручиваем
            setTimeout(() => {
                scrollToSelectedEpisode();
            }, 100);
        }
    }, [showEpisodesModal, selectedEpisode, scrollToSelectedEpisode]);

    useEffect(() => {
        if (userStatus) {
            setSelectedEpisode(userStatus.episodesWatched);
        }
    }, [userStatus]);

    useEffect(() => {
        if (showEpisodesModal && userStatus) {
            setSelectedEpisode(userStatus.episodesWatched);
        }
    }, [showEpisodesModal, userStatus]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const dropdown = document.querySelector('.dropdown-container');
            if (dropdown && !dropdown.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const formatDate = (dateString: string | null): string => {
        if (!dateString) return 'Не указана';

        try {
            const date = new Date(dateString);
            const formatter = new Intl.DateTimeFormat('ru-RU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
            return formatter.format(date);
        } catch (error) {
            return 'Неверная дата';
        }
    };

    const getSeasonDisplayName = (season: string | null): string => {
        if (!season) return 'Не указан';

        const seasons: { [key: string]: string } = {
            'WINTER': 'Зима',
            'SPRING': 'Весна',
            'SUMMER': 'Лето',
            'FALL': 'Осень'
        };

        return seasons[season] || season;
    };

    const fetchAnimeData = useCallback(async () => {
        try {
            const response = await api.get(`/anime/${id}`);
            setAnime(response.data);
        } catch (error) {
            console.error('Error fetching anime:', error);
            navigate('/');
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    const getAgeRatingDescription = (ageRating: string): string => {
        const descriptions: { [key: string]: string } = {
            'G': 'Нет возрастных ограничений',
            'PG': 'Рекомендуется присутствие родителей',
            'PG_13': 'Детям до 13 лет просмотр не желателен',
            'R_17': 'Лицам до 17 лет обязательно присутствие взрослого',
            'R_PLUS': 'Лицам до 17 лет просмотр запрещен'
        };

        return descriptions[ageRating] || ageRating;
    };

    const fetchUserStatus = useCallback(async () => {
        try {
            const response = await api.get(`/user/list`);
            const userList = response.data;

            const statusForThisAnime = userList.find(
                (item: any) => item.animeId === parseInt(id!)
            );

            setUserStatus(statusForThisAnime || null);
            setIsFavorite(statusForThisAnime?.isFavorite || false);

            if (statusForThisAnime) {
                setEpisodesInput(statusForThisAnime.episodesWatched.toString());
            }
        } catch (error) {
            console.error('Error fetching user status:', error);
            setUserStatus(null);
            setIsFavorite(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            fetchAnimeData();
            if (isAuthenticated) {
                fetchUserStatus();
            }
        }
    }, [id, isAuthenticated, fetchAnimeData, fetchUserStatus]);

    const handleAddToList = async (status: string) => {
        try {
            let episodesWatched = userStatus?.episodesWatched || 0;

            if (status === 'COMPLETED' && anime) {
                episodesWatched = anime.episodes;
            }

            await api.post('/user/add', null, {
                params: {
                    animeId: id,
                    status: status,
                    episodesWatched: episodesWatched,
                    rating: userStatus?.rating || null,
                    isFavorite: isFavorite
                }
            });

            await fetchUserStatus();
            fetchAnimeData();
            setShowDropdown(false);
        } catch (error) {
            console.error('Error adding to list:', error);
            alert('Error adding to list');
        }
    };

    const handleRateAnime = async (rating: number) => {
        try {
            await api.post('/user/add', null, {
                params: {
                    animeId: id,
                    status: userStatus?.status || 'WATCHING',
                    rating: rating,
                    episodesWatched: userStatus?.episodesWatched || 0,
                    isFavorite: isFavorite
                }
            });
            fetchUserStatus();
            fetchAnimeData();
        } catch (error) {
            console.error('Error rating anime:', error);
        }
    };

    const handleEpisodesSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!anime || !userStatus) return;

        try {
            await api.patch('/user/update-episodes', null, {
                params: {animeId: parseInt(id!), episodesWatched: selectedEpisode}
            });

            await fetchUserStatus();
            fetchAnimeData();
            setShowEpisodesModal(false);
        } catch (error) {
            console.error('Error:', error);
            alert('Ошибка при обновлении эпизодов');
        }
    };

    const handleStarHover = (rating: number) => {
        setHoverRating(rating);
    };

    const handleStarLeave = () => {
        setHoverRating(0);
    };

    const updateFavoriteStatus = async (favorite: boolean) => {
        try {
            await api.patch('/user/favorite', null, {
                params: {
                    animeId: id,
                    isFavorite: favorite
                }
            });
            setIsFavorite(favorite);
            await fetchUserStatus();
        } catch (error) {
            console.error('Error updating favorite status:', error);
        }
    };

    const shouldShowEpisodesButton = () => {
        if (!userStatus || !anime) return false;

        const activeStatuses = ['WATCHING', 'DROPPED', 'REWATCHING'];
        return activeStatuses.includes(userStatus.status);
    };

    const getStatusDisplayName = (status: string): string => {
        const displayNames: { [key: string]: string } = {
            'WATCHING': 'Смотрю',
            'COMPLETED': 'Просмотрено',
            'PLANNED': 'Запланировано',
            'DROPPED': 'Брошено',
            'REWATCHING': 'Пересматриваю'
        };
        return displayNames[status] || status;
    };

    const getStatusClass = (status: string): string => {
        const classMap: { [key: string]: string } = {
            'WATCHING': 'status-watching-anime',
            'COMPLETED': 'status-completed-anime',
            'PLANNED': 'status-planned-anime',
            'DROPPED': 'status-dropped-anime',
            'REWATCHING': 'status-rewatching-anime'
        };
        return classMap[status] || '';
    };

    const formatAgeRating = (rating: string): string => {
        if (!rating) return '';

        const ratingMap: { [key: string]: string } = {
            'PG_13': 'PG-13',
            'R_17': 'R-17',
            'R_PLUS': 'R+'
        };

        if (ratingMap[rating]) {
            return ratingMap[rating];
        }

        return rating;
    };

    // Функция для форматирования описания с учетом переносов строк
    const formatDescription = (description: string): React.ReactNode => {
        if (!description) return 'No description available.';

        return description.split('\n').map((paragraph, index) => (
            <React.Fragment key={index}>
                {paragraph.trim() && <p>{paragraph}</p>}
            </React.Fragment>
        ));
    };

    if (loading) {
        return <div className="loading-anime-page">Loading...</div>;
    }

    if (!anime) {
        return <div className="error-anime-page">Anime not found</div>;
    }

    return (
        <div className="page-anime-container">
            <div className="anime-content">
                <div className="anime-full-card">
                    {/* Левая колонка */}
                    <div className="anime-left-column">
                        <img
                            src={anime.imageUrl}
                            alt={anime.title}
                            className="anime-poster-large"
                            onError={(e) => {
                                e.currentTarget.src = "https://images.unsplash.com/photo-1578632767115-351597cf2477?w=300&h=450&fit=crop";
                            }}
                        />

                        {isAuthenticated && (
                            <div className="anime-actions-ap">
                                <div className="dropdown-container">
                                    <button
                                        onClick={() => setShowDropdown(!showDropdown)}
                                        className={`dropdown-toggle ${userStatus ? getStatusClass(userStatus.status) : ''}`}
                                    >
                                        <span className="current-status-text">
                                            {userStatus ? getStatusDisplayName(userStatus.status) : 'Добавить'}
                                        </span>
                                        {shouldShowEpisodesButton() && userStatus && (
                                            <>
                                                <div className="episodes-divider"/>
                                                <div
                                                    className="episodes-button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowEpisodesModal(true);
                                                    }}
                                                >
                                                    <Eye size={20}/>
                                                </div>
                                            </>
                                        )}
                                    </button>

                                    {showDropdown && (
                                        <div className="dropdown-menu">
                                            {statusOptions
                                                .filter(option => !userStatus || option !== userStatus.status)
                                                .map(option => (
                                                    <button
                                                        key={option}
                                                        onClick={() => {
                                                            handleAddToList(option);
                                                            setShowDropdown(false);
                                                        }}
                                                        className="dropdown-item"
                                                    >
                                                        {getStatusDisplayName(option)}
                                                    </button>
                                                ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Правая колонка */}
                    <div className="anime-right-column">
                        <div className="anime-ratings">
                            <div className="global-rating">
                                <Star className="rating-star global-star" size={28}/>
                                <div className="rating-content">
                                    <span className="rating-value">{anime.rating?.toFixed(1) || 'N/A'}</span>
                                </div>
                            </div>

                            <div className="rating-divider"></div>

                            {userStatus ? (
                                <>
                                    <div className="user-rating-container">
                                        <div className="main-star-with-selector">
                                            <span
                                                className={`rating-star user-star ${userStatus.rating ? 'rated' : ''}`}>
                                                <Star size={28}/>
                                            </span>
                                            <div className="rating-stars-selector">
                                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                                                    <span
                                                        key={rating}
                                                        className={`star-selector ${rating <= hoverRating ? 'hovered' : ''}
                                                        ${userStatus.rating && rating <= userStatus.rating ? 'rated' : ''}`}
                                                        onClick={() => handleRateAnime(rating)}
                                                        onMouseOver={() => handleStarHover(rating)}
                                                    >
                                                        <Star size={28}/>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="rating-content-user">
                                            {userStatus.rating ? (
                                                <>
                                                    <span className="rating-value-user">{userStatus.rating}</span>
                                                </>
                                            ) : (
                                                <span className="rating-value-text">Оцените<br/>аниме</span>
                                            )}
                                        </div>
                                    </div>

                                    <button
                                        className={`favorite-heart-btn ${isFavorite ? 'active' : ''}`}
                                        onClick={() => updateFavoriteStatus(!isFavorite)}
                                        title={isFavorite ? 'Убрать из любимого' : 'Добавить в любимое'}
                                    >
                                        <Heart
                                            size={30}
                                            color={isFavorite ? "#ff57c2" : "var(--text-primary)"}
                                        />
                                    </button>
                                </>
                            ) : (
                                <div className="user-rating-container">
                                    <span className="rating-star user-star">
                                        <Star size={28}/>
                                    </span>
                                    <div className="rating-content">
                                        <span className="rating-value-text">Добавьте<br/>чтобы оценить</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <h1 className="anime-title-main">{anime.title}</h1>

                        <div className="anime-meta-info">
                            <div className="meta-column labels-column">
                                <div className="meta-item">
                                    <span className="meta-label">Эпизоды:</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Жанры:</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Статус:</span>
                                </div>
                                {anime.sourceMaterial && (
                                    <div className="meta-item">
                                        <span className="meta-label">Первоисточник:</span>
                                    </div>
                                )}
                                <div className="meta-item">
                                    <span className="meta-label">Дата выхода:</span>
                                </div>
                                {anime.season && (
                                    <div className="meta-item">
                                        <span className="meta-label">Сезон:</span>
                                    </div>
                                )}
                                {anime.ageRating && (
                                    <div className="meta-item">
                                        <span className="meta-label">Рейтинг:</span>
                                    </div>
                                )}
                                {anime.studios && anime.studios.length > 0 && (
                                    <div className="meta-item">
                                        <span className="meta-label">Студия:</span>
                                    </div>
                                )}
                            </div>

                            <div className="meta-column values-column">
                                <div className="meta-item">
                                    <span className="meta-value">{anime.episodes}</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-value">{anime.genres?.join(', ') || 'Нет жанров'}</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-value">
                                        {anime.animeStatus === 'ONGOING' ? 'Онгоинг' :
                                            anime.animeStatus === 'COMPLETED' ? 'Завершен' :
                                                anime.animeStatus === 'UPCOMING' ? 'Анонсирован' : anime.animeStatus}
                                    </span>
                                </div>
                                {anime.sourceMaterial && (
                                    <div className="meta-item">
                                        <span className="meta-value">{anime.sourceMaterial}</span>
                                    </div>
                                )}
                                <div className="meta-item">
                                    <span className="meta-value">{formatDate(anime.releaseDate)}</span>
                                </div>
                                {anime.season && (
                                    <div className="meta-item">
                                        <span className="meta-value">{getSeasonDisplayName(anime.season)}</span>
                                    </div>
                                )}
                                {anime.ageRating && (
                                    <div className="meta-item">
                                        <div
                                            className="age-rating-with-tooltip"
                                            onMouseEnter={() => setHoverAgeRating(true)}
                                            onMouseLeave={() => setHoverAgeRating(false)}
                                        >
                                            <span className="meta-value underlined-text">
                                                {formatAgeRating(anime.ageRating)}
                                            </span>
                                            {hoverAgeRating && (
                                                <div className="age-rating-tooltip">
                                                    {getAgeRatingDescription(anime.ageRating)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {anime.studios && anime.studios.length > 0 && (
                                    <div className="meta-item">
                                        <span className="meta-value">{anime.studios.join(', ')}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Третий блок - Описание */}
                    <div className="anime-description-section">
                        <div className="description-content">
                            {formatDescription(anime.description)}
                        </div>
                    </div>
                </div>
            </div>

            {showEpisodesModal && anime && (
                <div className="modal-overlay-anime-page" onClick={() => setShowEpisodesModal(false)}>
                    <div className="modal-content-anime-page" onClick={(e) => e.stopPropagation()}>
                        <h3>Выберите просмотренный эпизод</h3>
                        <div className="episodes-picker">
                            <div className="episodes-progress">
                                Ваш прогресс: {userStatus?.episodesWatched} / {anime.episodes}
                            </div>

                            <div className="episodes-scroll-list" ref={episodesListRef}>
                                {Array.from({length: anime.episodes}, (_, i) => i + 1).map(ep => (
                                    <div
                                        key={ep}
                                        data-episode={ep}
                                        className={`episode-item ${selectedEpisode === ep ? 'selected' : ''}`}
                                        onClick={() => setSelectedEpisode(ep)}
                                    >
                                        {ep}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="modal-actions-anime-page">
                            <button type="button" onClick={() => setShowEpisodesModal(false)}>
                                Отменить
                            </button>
                            <button type="button" onClick={handleEpisodesSubmit}>
                                Сохранить
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnimePage;